const API_BASE_URL = import.meta.env.VITE_DOMAIN_TRACCAR || '';

const getFullUrl = (endpoint) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}${endpoint}`;
  }
  return endpoint;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = new Error(`HTTP Error: ${response.status}`);
    error.status = response.status;

    if (response.status === 400 && response.url.includes('/api/server')) {
      console.warn('[Traccar API] 400 en /api/server - ignorando (no crítico)');
      return null;
    }
    
    throw error;
  }
  
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return response.text();
};

const traccarApi = {
  login: async (email, password) => {
    try {
      console.log('[Traccar API] Attempting login:', email);
      console.log('[Traccar API] URL:', getFullUrl('/api/session'));
      
      const response = await fetch(getFullUrl('/api/session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ email, password }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Traccar API] Login failed:', response.status, errorText);
        throw new Error(`Login failed: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      console.log('[Traccar API] Login successful:', result.email);
      return result;
    } catch (error) {
      console.error('[Traccar API] Login error:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      const response = await fetch(getFullUrl('/api/session'), {
        method: 'DELETE',
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('[Traccar API] Logout error:', error);
      throw error;
    }
  },

  getSession: async () => {
    try {
      const response = await fetch(getFullUrl('/api/session'), {
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('[Traccar API] Get session error:', error);
      return null;
    }
  },

  getDevices: async () => {
    try {
      const response = await fetch(getFullUrl('/api/devices'), {
        credentials: 'include',
      });
      const data = await handleResponse(response);
      return data || [];
    } catch (error) {
      console.error('[Traccar API] Get devices error:', error);
      return [];
    }
  },

  getPositions: async (deviceId = null) => {
    try {
      const url = deviceId 
        ? getFullUrl(`/api/positions?deviceId=${deviceId}`)
        : getFullUrl('/api/positions');
      const response = await fetch(url, {
        credentials: 'include',
      });
      const data = await handleResponse(response);
      return data || [];
    } catch (error) {
      console.error('[Traccar API] Get positions error:', error);
      return [];
    }
  },

  getGroups: async () => {
    try {
      const response = await fetch(getFullUrl('/api/groups'), {
        credentials: 'include',
      });
      const data = await handleResponse(response);
      return data || [];
    } catch (error) {
      console.error('[Traccar API] Get groups error:', error);
      return [];
    }
  },

  getGeofences: async () => {
    try {
      const response = await fetch(getFullUrl('/api/geofences'), {
        credentials: 'include',
      });
      const data = await handleResponse(response);
      return data || [];
    } catch (error) {
      console.error('[Traccar API] Get geofences error:', error);
      return [];
    }
  },

  getReport: async (type, params) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(getFullUrl(`/api/reports/${type}?${queryString}`), {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });
      const data = await handleResponse(response);
      return data || [];
    } catch (error) {
      console.error('[Traccar API] Get report error:', error);
      return [];
    }
  },

  updateDevice: async (deviceId, updates) => {
    try {
      const response = await fetch(getFullUrl(`/api/devices/${deviceId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('[Traccar API] Update device error:', error);
      throw error;
    }
  },

  sendCommand: async (deviceId, command) => {
    try {
      const response = await fetch(getFullUrl('/api/commands/send'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deviceId, ...command }),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('[Traccar API] Send command error:', error);
      throw error;
    }
  },
};

export default traccarApi;
